import React, { forwardRef, useId } from "react";

export interface DatePickerProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, "size" | "type"> {
  label?: string;
  error?: string;
  helperText?: string;
  size?: "sm" | "md" | "lg";
  fullWidth?: boolean;
  minDate?: Date;
  maxDate?: Date;
}

const formatDateForInput = (date: Date | string | undefined): string => {
  if (!date) return "";
  const d = typeof date === "string" ? new Date(date) : date;
  if (isNaN(d.getTime())) return "";
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

/**
 * Arc-style DatePicker component
 * 
 * Features:
 * - Multiple sizes: sm, md, lg
 * - Label and helper text support
 * - Error state
 * - Min/max date constraints
 */
const DatePicker = forwardRef<HTMLInputElement, DatePickerProps>(
  (
    {
      label,
      error,
      helperText,
      size = "md",
      fullWidth = true,
      minDate,
      maxDate,
      className = "",
      disabled = false,
      required = false,
      id,
      value,
      ...props
    },
    ref
  ) => {
    const generatedId = useId();
    const inputId = id || generatedId;
    const hasError = !!error;

    const sizeStyles = {
      sm: {
        input: "py-1.5 px-3 text-sm",
        label: "text-xs mb-1.5",
      },
      md: {
        input: "py-2.5 px-4 text-sm",
        label: "text-sm mb-2",
      },
      lg: {
        input: "py-3.5 px-5 text-base",
        label: "text-sm mb-2",
      },
    };

    const currentSize = sizeStyles[size];

    const inputBaseStyles = `
      w-full
      rounded-arc-md
      font-medium
      text-content-primary
      bg-surface-elevated
      border border-arc-border
      transition-all duration-arc-normal ease-arc
      outline-none
      hover:border-arc-border-strong
      focus:border-transparent focus:ring-2 focus:ring-accent
      disabled:bg-surface-tertiary disabled:text-content-tertiary disabled:cursor-not-allowed disabled:border-arc-border
    `;

    const errorStyles = hasError
      ? "border-error hover:border-error focus:border-transparent focus:ring-2 focus:ring-error-main"
      : "";

    return (
      <div className={`${fullWidth ? "w-full" : ""} ${className}`}>
        {label && (
          <label
            htmlFor={inputId}
            className={`block font-semibold text-content-primary ${currentSize.label}`}
          >
            {label}
            {required && <span className="text-error ml-1">*</span>}
          </label>
        )}

        <input
          ref={ref}
          id={inputId}
          type="date"
          disabled={disabled}
          required={required}
          value={typeof value === "string" ? value : formatDateForInput(value as Date | undefined)}
          min={minDate ? formatDateForInput(minDate) : undefined}
          max={maxDate ? formatDateForInput(maxDate) : undefined}
          className={`
            ${inputBaseStyles}
            ${currentSize.input}
            ${errorStyles}
          `}
          {...props}
        />

        {(error || helperText) && (
          <p
            className={`mt-1.5 text-xs ${
              hasError ? "text-error" : "text-content-tertiary"
            }`}
          >
            {error || helperText}
          </p>
        )}
      </div>
    );
  }
);

DatePicker.displayName = "DatePicker";

export default DatePicker;



